import {
  Ban,
  Check,
  Clock,
  Download,
  Eye,
  FileSearch,
  Info,
  Pencil,
  RotateCcw,
  Trash2,
  X,
  type LucideIcon,
} from 'lucide-react';

export type TableActionVariant =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'destructive'
  | 'primary';

export type TableActionTone = 'soft' | 'solid';

export interface ActionPreset {
  icon: LucideIcon;
  variant: TableActionVariant;
  tooltip: string;
  /** Default tonal style. `soft` = pill tintado; `solid` = relleno fuerte. */
  tone?: TableActionTone;
  requiresConfirmation?: boolean;
}

/**
 * Mapeo central acción → icono + variante + tono + tooltip + confirmación.
 * Agregar una acción nueva = una entrada nueva acá + (opcional) una fila
 * en `docs/design-system/table-actions.md`. Nada más.
 *
 * Tono por default: `soft` (pill tintado siempre visible). El `solid`
 * primary se reserva para la acción de CREACIÓN (botón "+ Nuevo" o
 * "+ ítem"), de modo que en cada vista exista una sola identidad
 * azul-fuerte. `edit` conserva la asociación con primary pero como
 * `soft` para no competir con la creación.
 */
export const actionPresets = {
  view: { icon: Eye, variant: 'info', tooltip: 'Ver' },
  edit: { icon: Pencil, variant: 'primary', tooltip: 'Editar' },
  delete: {
    icon: Trash2,
    variant: 'destructive',
    tooltip: 'Eliminar',
    requiresConfirmation: true,
  },
  deactivate: {
    icon: Trash2,
    variant: 'destructive',
    tooltip: 'Desactivar',
    requiresConfirmation: true,
  },
  reactivate: { icon: RotateCcw, variant: 'success', tooltip: 'Reactivar' },
  approve: { icon: Check, variant: 'success', tooltip: 'Aprobar', requiresConfirmation: true },
  reject: { icon: X, variant: 'destructive', tooltip: 'Rechazar', requiresConfirmation: true },
  restore: { icon: RotateCcw, variant: 'neutral', tooltip: 'Restaurar' },
  cancel: { icon: Ban, variant: 'destructive', tooltip: 'Cancelar', requiresConfirmation: true },
  pending: { icon: Clock, variant: 'warning', tooltip: 'Marcar pendiente' },
  info: { icon: Info, variant: 'info', tooltip: 'Información' },
  download: { icon: Download, variant: 'neutral', tooltip: 'Descargar' },
  audit: { icon: FileSearch, variant: 'neutral', tooltip: 'Auditoría' },
} as const satisfies Record<string, ActionPreset>;

export type ActionPresetKey = keyof typeof actionPresets;
